import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Area, Brush, Line, XAxis } from 'recharts'
import { DEFAULT_LANGUAGE } from '../../../../../configs/language'
import { ChartContainer } from '../../../../common/chart/chartContainer'
import { TextEllipsisStaticLine } from '../../../../common/textEllipsis/TextEllipsisStaticLine'
import { formatByTheme } from '../../../../helpers/theme'
import { formatText, formatVal } from '../../../../helpers/value'
import { MARGIN_LEFT_CHART_DYNAMIC } from '../../../../tool/dynamicReport/constant'
import { PERIOD } from '../../../common/constant'
import { selectPeriod } from '../../../common/period/slice'
import { VALUE_SCORE } from '../../constant'
import { IDS_TREND_BY_PORT, TYPE_CHART_RISK } from '../constant'
import classes from '../index.module.scss'
import { selectActivePortfolio } from '../store/slice'

const LIST_TITLE = ['PORTFOLIO', 'MEDIAN_SCORE', 'INTERQUARTILE_RANGE']

export const ChartScore = ({
  isShowRange,
  data,
  listCheckBox,
  isDynamicReport,
  timePeriod,
  typeValue,
}) => {
  const ref = useRef()

  const periodNoDynamic = useSelector(selectPeriod)
  const period = isDynamicReport ? timePeriod : periodNoDynamic
  const activePortfolio2 = useSelector(
    selectActivePortfolio('activePortfolio2'),
  )
  const activePortfolio1 = useSelector(
    selectActivePortfolio('activePortfolio1'),
  )

  //Tooltip
  const renderCustomContent = ({ data, renderTooltipLabel }) => {
    return (
      <>
        <div>{renderTooltipLabel()}</div>
        <table className={classes.table}>
          <thead>
            <tr>
              {LIST_TITLE.map((item) => {
                return (
                  <th key={item}>
                    <Translate value={`portfolioScore.${item}`} />
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {Object.values(IDS_TREND_BY_PORT)
              .filter(
                (key) =>
                  data?.[`data${key}`] && listCheckBox.includes(`data${key}`),
              )
              .map((item, index) => (
                <tr key={index}>
                  <td>
                    <div style={{ maxWidth: 200 }}>
                      <TextEllipsisStaticLine
                        text={
                          item === IDS_TREND_BY_PORT.PORTFOLIO_FIRST
                            ? activePortfolio1.label
                            : item === IDS_TREND_BY_PORT.PORTFOLIO_SECOND
                            ? activePortfolio2.label
                            : formatText(data?.[`description${item}`])
                        }
                        lineClamp={2}
                        isI18n={false}
                      />
                    </div>
                  </td>
                  <td className="textCenter">
                    {formatText(data?.[`data${item}`])}
                  </td>
                  <td className="textCenter">
                    {formatText(data?.[`dataFloatBar${item}`]?.join(' - '))}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </>
    )
  }

  //UseEffect
  useEffect(() => {
    const timeOut = setTimeout(() => {
      const el = ref.current?.querySelector('.recharts-brush-slide')
      if (el) {
        el.setAttribute('rx', 6)
        clearTimeout(timeOut)
      }
    }, 300)
    return () => clearTimeout(timeOut)
  }, [])

  return (
    <>
      <div ref={ref}>
        <ChartContainer
          data={data}
          width="100%"
          height={340}
          margin={{
            left: isDynamicReport ? 16 + MARGIN_LEFT_CHART_DYNAMIC : 16,
          }}
          yAxis={[
            {
              yAxisId: 'left',
              dataKeys: TYPE_CHART_RISK.map((item) => item.dataKey),
              ticks:
                typeValue === VALUE_SCORE.CREDIT_SCORE
                  ? [0, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]
                  : [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
              tickMargin: 24,
              tickFormatter: (val) =>
                formatVal(val, DEFAULT_LANGUAGE, false, 0),
              axisLine: { stroke: formatByTheme('#9C9C9C') },
            },
          ]}
          tooltip={{
            schema: {
              renderCustomContent: renderCustomContent,
            },
          }}
        >
          <XAxis
            height={period === PERIOD.YEAR.value ? 30 : 60}
            dataKey="ticker"
            angle={period !== PERIOD.YEAR.value ? -30 : ''}
            textAnchor="middle"
            padding={{ left: 14, right: 30 }}
            axisLine={{ stroke: formatByTheme('#9C9C9C') }}
            tickLine={false}
            stroke="#9C9C9C"
            tickMargin={period !== PERIOD.YEAR.value ? 20 : 9}
            interval={0}
          />
          {TYPE_CHART_RISK.filter((key) =>
            listCheckBox.includes(key.dataKey),
          ).map((item) => (
            <Line
              key={item.id}
              yAxisId="left"
              dataKey={item.dataKey}
              stroke={item.color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
          {isShowRange &&
            TYPE_CHART_RISK.filter((key) =>
              listCheckBox.includes(key.dataKey),
            ).map((item, index) => (
              <Area
                key={index}
                yAxisId="left"
                dataKey={item.dataFloatBar}
                stroke="none"
                fill={item.color}
                fillOpacity={0.15}
                strokeWidth={2}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            ))}
          {period !== PERIOD.YEAR.value && data.length >= 12 && (
            <Brush
              className="customBrush"
              startIndex={data.length - 12}
              endIndex={data.length - 1}
              rx={4}
              height={6}
              stroke={isDynamicReport ? 'transparent' : '#BCBCBC'}
            />
          )}
        </ChartContainer>
      </div>
    </>
  )
}
